import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { useT } from "@shared/client/locale";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Lien profond vers une offre : `?offer=<id>` (le récapitulatif hebdomadaire
 * envoie sur les leads d'une offre précise). L'offre consultée vit dans un
 * cookie que seul `POST /api/offers/select` pose, après avoir vérifié qu'elle
 * appartient au compte : la porte l'appelle, recharge les vues, retire le
 * paramètre de l'adresse, et ne rend l'écran qu'ensuite — `fallback` en
 * attendant, pour ne pas montrer d'abord le pipeline de l'offre précédente.
 *
 * Une offre refusée (archivée depuis l'e-mail, lien ouvert sous un autre
 * compte, réseau coupé) laisse l'offre consultée, et l'écran le dit en une
 * ligne plutôt que de montrer une autre offre sans prévenir.
 */
export function OfferLinkGate({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const requested = params.get("offer");
  const offerId = requested && UUID_PATTERN.test(requested) ? requested : null;
  // La dernière offre demandée par un lien, et si l'API l'a acceptée.
  const [handled, setHandled] = useState<{ offerId: string; ok: boolean } | null>(null);
  const switching = offerId !== null && handled?.offerId !== offerId;

  useEffect(() => {
    if (!offerId) return;
    let cancelled = false;
    void (async () => {
      const response = await fetch("/api/offers/select", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId }),
      }).catch(() => null);
      if (cancelled) return;
      const ok = Boolean(response?.ok);
      if (ok) await queryClient.invalidateQueries();
      if (cancelled) return;
      setHandled({ offerId, ok });
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("offer");
          return next;
        },
        { replace: true },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [offerId, queryClient, setParams]);

  if (switching) return <>{fallback}</>;
  return (
    <>
      {handled && !handled.ok && (
        <Alert className="mb-6.5" role="status">
          <AlertDescription>{t.leads.offerLink.unavailable}</AlertDescription>
        </Alert>
      )}
      {children}
    </>
  );
}
